import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { AddressResponse } from "../../api/addresses/types";
import { useDeliveriesByAddressAll } from "../../api/deliveries";
import "./AddressDeliveryHistoryModal.css";

export interface AddressDeliveryHistoryModalProps {
    open: boolean;
    address: AddressResponse | null;
    onClose: () => void;
}

function formatAddressLine(a: AddressResponse): string {
    const parts = [a.street, a.number, a.apartment].filter(
        (p): p is string => typeof p === "string" && p.trim().length > 0
    );
    return parts.join(" ");
}

export const AddressDeliveryHistoryModal = ({
    open,
    address,
    onClose
}: AddressDeliveryHistoryModalProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const addressPointId = open && address ? address.id : undefined;
    const { data, isLoading, isError, error } = useDeliveriesByAddressAll(addressPointId);
    const mouseDownOnOverlay = useRef(false);

    useEffect(() => {
        if (!open) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open, onClose]);

    if (!open || !address) {
        return <></>;
    }

    const locale = i18n.language?.startsWith("sv") ? "sv-SE" : undefined;

    return (
        <div
            className="address-delivery-history-modal__overlay"
            onMouseDown={(ev) => { mouseDownOnOverlay.current = ev.target === ev.currentTarget; }}
            onMouseUp={(ev) => {
                if (mouseDownOnOverlay.current && ev.target === ev.currentTarget) onClose();
                mouseDownOnOverlay.current = false;
            }}
        >
            <div
                className="address-delivery-history-modal__content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="address-delivery-history-modal__title"
                aria-modal="true"
            >
                <header className="address-delivery-history-modal__header">
                    <h2 id="address-delivery-history-modal__title" className="address-delivery-history-modal__title">
                        {t("map.addressDeliveryHistory.title")}
                    </h2>
                    <p className="address-delivery-history-modal__subtitle">
                        {formatAddressLine(address)} · {address.postalCode} {address.city}
                    </p>
                </header>

                <div className="address-delivery-history-modal__body">
                    {isLoading && (
                        <div className="address-delivery-history-modal__loading">
                            {t("map.addressDeliveryHistory.loading")}
                        </div>
                    )}
                    {isError && (
                        <div className="address-delivery-history-modal__error">
                            {error instanceof Error ? error.message : t("map.addressDeliveryHistory.error")}
                        </div>
                    )}
                    {!isLoading && !isError && data && data.length === 0 && (
                        <div className="address-delivery-history-modal__empty">
                            {t("map.addressDeliveryHistory.empty")}
                        </div>
                    )}
                    {!isLoading &&
                        !isError &&
                        data &&
                        data.map((d) => {
                            const when = new Date(d.deliveredAt);
                            const dateStr = Number.isNaN(when.getTime())
                                ? d.deliveredAt
                                : when.toLocaleString(locale, {
                                      dateStyle: "medium",
                                      timeStyle: "short"
                                  });
                            const flyer =
                                d.flyerName?.trim() ||
                                t("map.addressDeliveryHistory.unknownFlyer");
                            const campaign =
                                d.campaignName?.trim() ||
                                t("map.addressDeliveryHistory.unknownCampaign");
                            const qty =
                                d.quantity != null && d.quantity > 1
                                    ? t("map.addressDeliveryHistory.quantity", { count: d.quantity })
                                    : null;
                            const deliveredBy = d.recordedByName?.trim() || null;
                            // Campaign and flyer are often named the same ("Närodlat" in
                            // "Närodlat"); repeating it just costs a line.
                            const showCampaign = campaign !== flyer;
                            const meta = [dateStr, qty, deliveredBy].filter(Boolean).join(" · ");

                            return (
                                <div key={d.id} className="address-delivery-history-modal__row">
                                    <div className="address-delivery-history-modal__headline">
                                        <span className="address-delivery-history-modal__flyer">{flyer}</span>
                                        {showCampaign && (
                                            <span className="address-delivery-history-modal__campaign">{campaign}</span>
                                        )}
                                    </div>
                                    <div className="address-delivery-history-modal__meta">{meta}</div>
                                </div>
                            );
                        })}
                </div>

                <footer className="address-delivery-history-modal__footer">
                    <button
                        type="button"
                        className="address-delivery-history-modal__close"
                        onClick={onClose}
                    >
                        {t("map.addressDeliveryHistory.close")}
                    </button>
                </footer>
            </div>
        </div>
    );
};
